import client from "@/client/publicApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { GetAllClientMarketInstrumentsQueryResponse, GetAllClientMarketInstrumentsPathParams, GetAllClientMarketInstrumentsQueryParams } from "../types/GetAllClientMarketInstruments";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type GetAllClientMarketInstrumentsClient = typeof client<GetAllClientMarketInstrumentsQueryResponse, never, never>;
type GetAllClientMarketInstruments = {
    data: GetAllClientMarketInstrumentsQueryResponse;
    error: never;
    request: never;
    pathParams: GetAllClientMarketInstrumentsPathParams;
    queryParams: GetAllClientMarketInstrumentsQueryParams;
    headerParams: never;
    response: GetAllClientMarketInstrumentsQueryResponse;
    client: {
        parameters: Partial<Parameters<GetAllClientMarketInstrumentsClient>[0]>;
        return: Awaited<ReturnType<GetAllClientMarketInstrumentsClient>>;
    };
};
export const getAllClientMarketInstrumentsQueryKey = (clientCode: GetAllClientMarketInstrumentsPathParams["clientCode"], marketCode: GetAllClientMarketInstrumentsPathParams["marketCode"], params?: GetAllClientMarketInstruments["queryParams"]) => [{ url: "/api/clients/:clientCode/markets/:marketCode/instruments", params: { clientCode: clientCode, marketCode: marketCode } }, ...(params ? [params] : [])] as const;
export type GetAllClientMarketInstrumentsQueryKey = ReturnType<typeof getAllClientMarketInstrumentsQueryKey>;
export function getAllClientMarketInstrumentsQueryOptions(clientCode: GetAllClientMarketInstrumentsPathParams["clientCode"], marketCode: GetAllClientMarketInstrumentsPathParams["marketCode"], params?: GetAllClientMarketInstruments["queryParams"], options: GetAllClientMarketInstruments["client"]["parameters"] = {}) {
    const queryKey = getAllClientMarketInstrumentsQueryKey(clientCode, marketCode, params);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetAllClientMarketInstruments["data"], GetAllClientMarketInstruments["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/markets/${marketCode}/instruments`,
                params,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets all intruments for a given market and client
 * @summary Get all
 * @link /api/clients/:clientCode/markets/:marketCode/instruments
 */
export function useGetAllClientMarketInstruments<TData = GetAllClientMarketInstruments["response"], TQueryData = GetAllClientMarketInstruments["response"], TQueryKey extends QueryKey = GetAllClientMarketInstrumentsQueryKey>(clientCode: GetAllClientMarketInstrumentsPathParams["clientCode"], marketCode: GetAllClientMarketInstrumentsPathParams["marketCode"], params?: GetAllClientMarketInstruments["queryParams"], options: {
    query?: Partial<QueryObserverOptions<GetAllClientMarketInstruments["response"], GetAllClientMarketInstruments["error"], TData, TQueryData, TQueryKey>>;
    client?: GetAllClientMarketInstruments["client"]["parameters"];
} = {}): UseQueryResult<TData, GetAllClientMarketInstruments["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getAllClientMarketInstrumentsQueryKey(clientCode, marketCode, params);
    const query = useQuery({
        ...getAllClientMarketInstrumentsQueryOptions(clientCode, marketCode, params, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, GetAllClientMarketInstruments["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const getAllClientMarketInstrumentsSuspenseQueryKey = (clientCode: GetAllClientMarketInstrumentsPathParams["clientCode"], marketCode: GetAllClientMarketInstrumentsPathParams["marketCode"], params?: GetAllClientMarketInstruments["queryParams"]) => [{ url: "/api/clients/:clientCode/markets/:marketCode/instruments", params: { clientCode: clientCode, marketCode: marketCode } }, ...(params ? [params] : [])] as const;
export type GetAllClientMarketInstrumentsSuspenseQueryKey = ReturnType<typeof getAllClientMarketInstrumentsSuspenseQueryKey>;
export function getAllClientMarketInstrumentsSuspenseQueryOptions(clientCode: GetAllClientMarketInstrumentsPathParams["clientCode"], marketCode: GetAllClientMarketInstrumentsPathParams["marketCode"], params?: GetAllClientMarketInstruments["queryParams"], options: GetAllClientMarketInstruments["client"]["parameters"] = {}) {
    const queryKey = getAllClientMarketInstrumentsSuspenseQueryKey(clientCode, marketCode, params);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetAllClientMarketInstruments["data"], GetAllClientMarketInstruments["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/markets/${marketCode}/instruments`,
                params,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets all intruments for a given market and client
 * @summary Get all
 * @link /api/clients/:clientCode/markets/:marketCode/instruments
 */
export function useGetAllClientMarketInstrumentsSuspense<TData = GetAllClientMarketInstruments["response"], TQueryKey extends QueryKey = GetAllClientMarketInstrumentsSuspenseQueryKey>(clientCode: GetAllClientMarketInstrumentsPathParams["clientCode"], marketCode: GetAllClientMarketInstrumentsPathParams["marketCode"], params?: GetAllClientMarketInstruments["queryParams"], options: {
    query?: Partial<UseSuspenseQueryOptions<GetAllClientMarketInstruments["response"], GetAllClientMarketInstruments["error"], TData, TQueryKey>>;
    client?: GetAllClientMarketInstruments["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, GetAllClientMarketInstruments["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getAllClientMarketInstrumentsSuspenseQueryKey(clientCode, marketCode, params);
    const query = useSuspenseQuery({
        ...getAllClientMarketInstrumentsSuspenseQueryOptions(clientCode, marketCode, params, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, GetAllClientMarketInstruments["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}